/**
 * Thin REST wrapper around the Auth0 Authentication API. Pure functions —
 * no React, no global state. The AuthProvider composes these into a flow.
 *
 * Endpoints used:
 *   POST /passwordless/start       — send a magic-link email
 *   POST /oauth/token              — exchange code for tokens (PKCE)
 *                                  — refresh tokens
 *   POST /oauth/revoke             — revoke a refresh token on logout
 *
 * No client secret involved (SPA with PKCE, RFC 8252).
 */

export interface Auth0Config {
  domain: string;
  clientId: string;
  audience: string;
}

export interface TokenResponse {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  scope?: string;
  expires_in: number;
  token_type: "Bearer";
}

export interface PasswordlessStartParams {
  email: string;
  /** Stored client-side; sent as state in `authParams`. */
  state: string;
  /** Stored client-side; sha256 sent as code_challenge. */
  codeChallenge: string;
  redirectUri: string;
  scope?: string;
}

interface ApiError extends Error {
  status: number;
  body?: unknown;
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    const err = new Error(
      (errBody as { error_description?: string; error?: string } | null)?.error_description ||
        (errBody as { error?: string } | null)?.error ||
        res.statusText,
    ) as ApiError;
    err.status = res.status;
    err.body = errBody;
    throw err;
  }
  return res.json() as Promise<T>;
}

/**
 * Trigger a magic-link email. Auth0 stores the verification code server-side
 * keyed on (email, state); when the user clicks the link in the inbox they
 * land at `redirectUri?code=...&state=...`.
 */
export async function passwordlessStart(
  cfg: Auth0Config,
  params: PasswordlessStartParams,
): Promise<{ _id: string; email: string }> {
  return postJson(`https://${cfg.domain}/passwordless/start`, {
    client_id: cfg.clientId,
    connection: "email",
    email: params.email,
    send: "link",
    authParams: {
      response_type: "code",
      redirect_uri: params.redirectUri,
      audience: cfg.audience,
      scope: params.scope ?? "openid profile email offline_access",
      state: params.state,
      code_challenge: params.codeChallenge,
      code_challenge_method: "S256",
    },
  });
}

/**
 * Exchange the auth code (delivered by the magic link) for an access +
 * refresh + id token set. Caller validates state separately.
 */
export async function exchangeCode(
  cfg: Auth0Config,
  code: string,
  codeVerifier: string,
  redirectUri: string,
): Promise<TokenResponse> {
  return postJson(`https://${cfg.domain}/oauth/token`, {
    grant_type: "authorization_code",
    client_id: cfg.clientId,
    code,
    code_verifier: codeVerifier,
    redirect_uri: redirectUri,
  });
}

/** Use a refresh token to mint a new access token (with refresh-token rotation). */
export async function refreshTokens(
  cfg: Auth0Config,
  refreshToken: string,
): Promise<TokenResponse> {
  return postJson(`https://${cfg.domain}/oauth/token`, {
    grant_type: "refresh_token",
    client_id: cfg.clientId,
    refresh_token: refreshToken,
  });
}

/** Revoke a refresh token on logout. Best-effort — failure is non-fatal. */
export async function revokeRefreshToken(
  cfg: Auth0Config,
  refreshToken: string,
): Promise<void> {
  try {
    await fetch(`https://${cfg.domain}/oauth/revoke`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client_id: cfg.clientId, token: refreshToken }),
    });
  } catch {
    // Best-effort; user is logging out anyway.
  }
}

/**
 * Decode a JWT body without verifying its signature. We use this only for
 * non-security-critical claims display (email shown in AppBar). All
 * signature verification happens on the backend against Auth0 JWKS.
 */
export function decodeJwt<T = Record<string, unknown>>(token: string): T | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const padded = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padding = padded.length % 4 ? 4 - (padded.length % 4) : 0;
    const json = atob(padded + "=".repeat(padding));
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}
